import { useRef, useState } from 'react';
import axios from 'axios';
import { axiosInstance } from '@/api/client';

// Must stay in sync with backend constants in views/feedback.py
const MAX_TOTAL_BYTES = 20 * 1024 * 1024;
const MAX_FILES = 10;
const ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/webm,video/mpeg';

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

interface Props {
  onClose: () => void;
}

export default function FeedbackModal({ onClose }: Props) {
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const totalSize = files.reduce((sum, f) => sum + f.size, 0);
  const overLimit = totalSize > MAX_TOTAL_BYTES;
  const overCount = files.length > MAX_FILES;

  function addFiles(incoming: FileList | null) {
    if (!incoming) return;
    setFiles((prev) => {
      const existing = new Set(prev.map((f) => `${f.name}:${f.size}`));
      const next = [...prev];
      for (const f of Array.from(incoming)) {
        if (!existing.has(`${f.name}:${f.size}`)) next.push(f);
      }
      return next;
    });
  }

  function removeFile(index: number) {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    addFiles(e.target.files);
    e.target.value = '';
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!message.trim() || overLimit || overCount) return;

    setSubmitting(true);
    setError('');

    const formData = new FormData();
    formData.append('message', message.trim());
    files.forEach((f) => formData.append('attachments', f));

    try {
      await axiosInstance.post('/api/feedback/', formData);
      setSubmitted(true);
    } catch (err: unknown) {
      const fallback = 'Something went wrong. Try again?';
      const msg = axios.isAxiosError(err) ? (err.response?.data?.error ?? fallback) : fallback;
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 p-8"
        onClick={(e) => e.stopPropagation()}
      >
        {submitted ? (
          <div className="text-center">
            <div className="text-5xl mb-4">📬</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Got it!</h2>
            <p className="text-gray-500 mb-6">Message received. We'll pretend to read it.</p>
            <button
              onClick={onClose}
              className="bg-gray-900 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-gray-700 transition-colors w-full"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <div className="text-4xl mb-3">📣</div>
              <h2 className="text-xl font-bold text-gray-900">found a bug?</h2>
              <p className="text-gray-500 text-sm mt-1 leading-relaxed">
                found a feature? got feedback? just want to send a random email to someone who may
                or may not read it? whatever — go for it.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="what's on your mind..."
                rows={4}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 resize-none"
              />

              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={ACCEPT}
                  className="hidden"
                  onChange={handleFileChange}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-sm text-gray-600 border border-dashed border-gray-300 rounded-lg px-4 py-2 hover:border-gray-500 hover:text-gray-900 transition-colors w-full"
                >
                  + attach screenshots / images / videos
                </button>
              </div>

              {files.length > 0 && (
                <ul className="space-y-1.5">
                  {files.map((f, i) => (
                    <li
                      key={`${f.name}:${f.size}`}
                      className="flex items-center justify-between text-xs bg-gray-50 rounded-lg px-3 py-2"
                    >
                      <span className="truncate text-gray-700 mr-2">{f.name}</span>
                      <span className="flex items-center gap-2 shrink-0">
                        <span className="text-gray-400">{formatBytes(f.size)}</span>
                        <button
                          type="button"
                          onClick={() => removeFile(i)}
                          className="text-gray-400 hover:text-red-500 transition-colors font-bold"
                        >
                          ×
                        </button>
                      </span>
                    </li>
                  ))}
                  <li className={`text-xs px-1 ${overCount || overLimit ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                    {files.length}/{MAX_FILES} files · {formatBytes(totalSize)} / {formatBytes(MAX_TOTAL_BYTES)}
                    {overCount && ' — too many files, remove some'}
                    {!overCount && overLimit && ' — too large, remove some files'}
                  </li>
                </ul>
              )}

              {error && <p className="text-sm text-red-500">{error}</p>}

              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 border border-gray-300 text-gray-700 px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  nah, never mind
                </button>
                <button
                  type="submit"
                  disabled={submitting || !message.trim() || overLimit || overCount}
                  className="flex-1 bg-gray-900 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'sending...' : 'send it'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
